import type { AdSet, TargetingConfig } from '../../types/metaAdDraft';
import type { SavedAudience, SavedAudienceSource } from '../../types/savedAudience';

// Pure helpers for the builder's Saved-Audience Save + Apply actions (Feature B,
// Phase 1). No I/O — the components own the network calls.

// Derive the source coords to save from an ad set's current targeting. Extracted
// targeting carries the campaign/ad-set it came from → a concrete source; manual
// or preset targeting has no Meta source → null.
export function buildSavedAudienceSource(
  targeting: TargetingConfig,
  fbAdAccountId: string | null,
): SavedAudienceSource | null {
  if (targeting.sourceType !== 'extracted') return null;
  if (!targeting.sourceCampaignId) return null;
  return {
    adAccountId: fbAdAccountId ?? '',
    campaignId: targeting.sourceCampaignId,
    adSetId: targeting.sourceAdSetId ?? '',
    campaignName: targeting.sourceCampaignName ?? null,
  };
}

// The default name to pre-fill in the Save prompt. Extracted targeting defaults to
// the source ad-set / campaign label; otherwise a sensible account+date fallback.
export function defaultSavedAudienceName(
  targeting: TargetingConfig,
  opts: { accountName: string; today: string },
): string {
  if (targeting.sourceType === 'extracted') {
    const fromSource = targeting.sourceAdSetName || targeting.sourceCampaignName;
    if (fromSource && fromSource.trim() !== '') return fromSource.trim();
  }
  const account = opts.accountName.trim() || 'Account';
  return `${account} audience — ${opts.today}`;
}

// Apply a saved audience to an ad set: drop its (placement-free) targeting in as a
// starting point. Fully editable afterwards — apply locks nothing.
export function applySavedAudience(adSet: AdSet, audience: SavedAudience): AdSet {
  return { ...adSet, targeting: audience.targeting };
}

// An extracted audience keeps its Meta targeting verbatim in rawPayload (Phase 1
// rule: extracted stays locked/as-is). Used to badge it in the hub list. source is
// non-null only for extracted, so either signal flags it.
export function isExtractedAudience(audience: SavedAudience): boolean {
  return audience.targeting.sourceType === 'extracted' || audience.source !== null;
}

// A compact one-line summary of MANUAL targeting (geo count / age / gender /
// interests) for the hub list. Extracted audiences store everything in rawPayload
// (the structured fields stay at defaults), so audienceTargetingSummary names the
// source campaign for those instead of summarising empty fields.
export function summarizeManualTargeting(t: TargetingConfig): string {
  const geoCount =
    t.countries.length +
    t.cities.length +
    t.regions.length +
    t.places.length +
    t.neighborhoods.length +
    t.zips.length +
    t.customLocations.length;
  const gender = t.gender === 'all' ? 'All genders' : t.gender === 'male' ? 'Men' : 'Women';
  const parts = [
    geoCount > 0 ? `${geoCount} location${geoCount === 1 ? '' : 's'}` : 'Any location',
    `Age ${t.ageMin}–${t.ageMax}`,
    gender,
  ];
  if (t.interests.length > 0) {
    parts.push(`${t.interests.length} interest${t.interests.length === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

// One-line targeting summary for a saved audience row. Names the source campaign
// for extracted audiences; summarises geo/age/gender/interests for manual ones.
export function audienceTargetingSummary(audience: SavedAudience): string {
  const t = audience.targeting;
  if (isExtractedAudience(audience)) {
    const name = audience.source?.campaignName || t.sourceCampaignName || t.sourceAdSetName;
    return name ? `Extracted from ${name}` : 'Extracted targeting';
  }
  return summarizeManualTargeting(t);
}
